<template>
  <div>
    <h6 class="font-italic mt-1 mb-4">
      {{ $t('reports_ui.route_assessment_metrics_report.two_day_delay_note') }}
    </h6>
    <v-form>
      <v-text-field
        v-if="localReport.id"
        v-model="localReport.name"
        :label="$tc('name')"
        :error-messages="nameErrors"
        prepend-inner-icon="mdi-form-textbox"/>
      <vf-autocomplete
        :label="$tc('customer', 2)"
        v-model="reportCustomers"
        :items="availableCustomers"
        return-object
        all/>
      <v-select
        :label="$t('period')"
        v-model="reportPeriod"
        :items="reportPeriodOptions"
        @input="reportPeriodChanged" />
      <div v-if="reportPeriod === '1 day'">
        <div class="d-flex align-center">
          <date-picker
            v-model="startDate"
            :max="maxDate"
            :timezone="timezone"
            :output-moment="true"
            :label="$t('start_date')"
            prepend-inner-icon="mdi-calendar"
            :error-messages="dateRangeErrors"
            :reset-on-timezone-change="context === 'create'"/>
          <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
          <date-picker
            v-model="endDate"
            :max="maxDate"
            :timezone="timezone"
            :output-moment="true"
            :end-of-day="true"
            :label="$t('end_date')"
            prepend-inner-icon="mdi-calendar"
            :error="dateRangeErrors.length > 0"
            :reset-on-timezone-change="context === 'create'"/>
        </div>
        <date-selector-shortcuts :lowerBound.sync="startDate" :upperBound.sync="endDate" :timezone="timezone"/>
      </div>
      <div v-if="reportPeriod === '1 week'" class="d-flex align-center">
        <date-picker
          v-model="startDate"
          :max="maxDate"
          :timezone="timezone"
          :output-moment="true"
          :label="$t('start_date')"
          prepend-inner-icon="mdi-calendar"
          :error-messages="dateRangeErrors"
          :allowed-dates="restrictToStartOfWeek"
          :reset-on-timezone-change="context === 'create'"/>
        <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
        <date-picker
          v-model="endDate"
          :max="maxDate"
          :timezone="timezone"
          :output-moment="true"
          :end-of-day="true"
          :label="$t('end_date')"
          prepend-inner-icon="mdi-calendar"
          :error="dateRangeErrors.length > 0"
          :allowed-dates="restrictToStartOfWeek"
          :reset-on-timezone-change="context === 'create'"/>
      </div>
      <div v-if="reportPeriod === '1 month'" class="d-flex align-center">
        <date-picker
          v-model="startDate"
          type="month"
          :max="maxDate"
          :timezone="timezone"
          :output-moment="true"
          :label="$t('start_date')"
          prepend-inner-icon="mdi-calendar"
          :error-messages="dateRangeErrors"
          :reset-on-timezone-change="context === 'create'"/>
        <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
        <date-picker
          v-model="endDate"
          type="month"
          :max="maxDate"
          :timezone="timezone"
          :output-moment="true"
          :label="$t('end_date')"
          prepend-inner-icon="mdi-calendar"
          :error="dateRangeErrors.length > 0"
          :reset-on-timezone-change="context === 'create'"/>
      </div>
    </v-form>
  </div>

</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import ReportFormMixin from '@/components/report/ReportFormMixin'
import DatePicker from '@/components/shared/DatePicker'
import DateSelectorShortcuts from '@/components/report/DateSelectorShortcuts'
import VfAutocomplete from '@/components/shared/VfAutocomplete'

import Moment from 'moment-timezone'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { get, cloneDeep, forEach, find, map } from 'lodash'

export default {
  name: 'RouteAssessmentMetricsReportForm',
  components: {
    DatePicker,
    DateSelectorShortcuts,
    VfAutocomplete
  },
  mixins: [
    CurrentUserMixin,
    ApiUtilitiesMixin,
    ValidationMixin,
    ReportFormMixin
  ],
  validations: {
    reportCustomers: {
      required
    },
    localReport: {
      name: {
        required: requiredIf(function () {
          return this.localReport.id != null
        })
      }
    },
    dateRange: {
      rangeIsValid: function (value) {
        return this.startDate != null &&
          this.endDate != null &&
          this.startDate.isValid() &&
          this.endDate.isValid() &&
          this.endDate >= this.startDate
      }
    }
  },
  props: {
    report: {
      type: Object
    },
    context: {
      type: String,
      default: 'create'
    }
  },
  data () {
    return {
      reportType: 'RouteAssessmentMetricsReport',
      localReport: cloneDeep(this.report),
      action: this.context,
      startDate: null,
      endDate: null,
      reportPeriod: null,
      reportCustomers: [],
      saving: false,
      reportPeriodOptions: [
        { text: this.$t('daily'), value: '1 day' },
        { text: this.$t('weekly'), value: '1 week' },
        { text: this.$t('monthly'), value: '1 month' }
      ]
    }
  },
  computed: {
    /**
     * Get the timezone that the report is operating under. If a new report,
     * default to the current customer context's timezone.
     */
    timezone () {
      return get(this.report, 'parameters.timezone', this.customerContext.timezone())
    },
    /**
     * Lists customers available to the current user (customers in their scope)
     */
    availableCustomers () {
      if (this.customers === undefined) {
        return []
      }

      const inScopeCustomers = []
      this.customers.forEach(function (customer) {
        inScopeCustomers.push({ text: customer.name, value: customer.id })
      })

      return inScopeCustomers
    },
    /**
     * The date picker options used in the 'monthly' LegacyDatePicker
     */
    monthlyDatePickerOptions () {
      return {
        format: 'MM/YYYY',
        viewMode: 'months',
        locale: this.$i18n.locale,
        useCurrent: false,
        maxDate: Moment().tz(this.timezone).startOf('month')
      }
    },
    /**
     * The maximum date selectable in the date pickers.
     */
    maxDate () {
      return Moment.tz(this.timezone).subtract(2, 'days').toISOString()
    },
    nameErrors () {
      return this.validationErrors([{
        prop: this.$v.localReport.name,
        message: this.$t('required')
      }])
    },
    dateRangeErrors () {
      return this.validationErrors([{
        prop: this.$v.dateRange,
        message: this.$t('invalid_date_range')
      }])
    }
  },
  methods: {
    populateReportForm () {
      this.preSelectDates()
      this.preSelectCustomers()
    },
    buildReportParameters () {
      const reportParameters = {}

      reportParameters.startDate = this.startDate.format()
      reportParameters.endDate = this.endDate.format()
      reportParameters.customerIds = map(this.reportCustomers, x => x.value)
      reportParameters.timezone = this.timezone
      reportParameters.periodInterval = this.reportPeriod

      return reportParameters
    },
    defaultReportName () {
      return this.$t('reports_ui.route_assessment_metrics_report.name')
    },
    /**
     * Pull the date values selected from a previous report (if defined) and set the
     * state of this form to match
     *
     * @var ReportRecord report
     * @return undefined
     */
    preSelectDates () {
      const preSelectedStartDate = get(this, 'report.parameters.startDate')
      if (preSelectedStartDate) {
        this.startDate = Moment.tz(preSelectedStartDate, this.timezone)
      }
      const preSelectedEndDate = get(this, 'report.parameters.endDate')
      if (preSelectedEndDate) {
        this.endDate = Moment.tz(preSelectedEndDate, this.timezone)
      }
    },
    /**
     * Prepopulate customers from report state.
     */
    preSelectCustomers () {
      forEach(get(this, 'report.customer_scope'), customerId => {
        const customer = find(this.availableCustomers, c => {
          return c.value === customerId
        })
        if (customer) {
          this.reportCustomers.push({ text: customer.text, value: customer.value })
        }
      })
    },
    /**
     * Restrict weekly report picker to start of the week (Sunday)
     */
    restrictToStartOfWeek: val => Moment(val).day() === 0,
    /**
     * When the report period changes due to user input, reset the date values.
     */
    reportPeriodChanged () {
      this.startDate = null
      this.endDate = null
    }
  },
  created () {
    this.reportPeriod = get(this, 'report.parameters.periodInterval', '1 day')
    this.preSelectDates()
    this.preSelectCustomers()
  },
  watch: {
    /**
     * The report supplied can change when we clone from the ReportCreate
     * component. Watch for changes and update the form accordingly
     */
    report () {
      this.reportPeriod = get(this, 'report.parameters.periodInterval', '1 day')
      this.preSelectDates()
      this.preSelectCustomers()
    }
  }
}
</script>

<style scoped>

</style>
